import React, { useState, useEffect, useMemo } from "react";
import { Box, Container } from "@mui/material";

import FlowEntryStep from "./steps/entry/FlowEntryStep";
import ContextStep from "./steps/unit-planning/context/ContextStep";
import UnitSelectionStep from "./steps/unit-planning/selection/UnitSelectionStep";
import UnitSituationStep from "./steps/unit-planning/situation/UnitSituationStep";
import SessionInputStep from "./steps/session-generation/input/SessionInputStep";
import SessionSequenceStep from "./steps/session-generation/sequence/SessionSequenceStep";
import SessionBuildStep from "./steps/session-generation/build/SessionBuildStep";

import logoSac from "../../../assets/PlanSAC.png";
import HeroHeader from "../../HeroHeader";

const sxPage = { px: { xs: 2, sm: 4, md: 8, lg: 12 } };
const STEP_IDS = {
    FLOW_ENTRY: "flow-entry",
    CONTEXT: "context",
    UNIT_SELECTION: "unit-selection",
    UNIT_SITUATION: "unit-situation",
    SESSION_INPUT: "session-input",
    SESSION_SEQUENCE: "session-sequence",
    SESSION_BUILD: "session-build",
};

const FLOW_BY_ROLE = {
    ADMIN: [
        STEP_IDS.FLOW_ENTRY,
        STEP_IDS.CONTEXT,
        STEP_IDS.UNIT_SELECTION,
        STEP_IDS.UNIT_SITUATION,
        STEP_IDS.SESSION_INPUT,
        STEP_IDS.SESSION_SEQUENCE,
        STEP_IDS.SESSION_BUILD,
    ],
    DIRECTOR: [
        STEP_IDS.FLOW_ENTRY,
        STEP_IDS.CONTEXT,
        STEP_IDS.UNIT_SELECTION,
        STEP_IDS.UNIT_SITUATION,
        STEP_IDS.SESSION_INPUT,
        STEP_IDS.SESSION_SEQUENCE,
        STEP_IDS.SESSION_BUILD,
    ],
    PROFESOR: [
        STEP_IDS.SESSION_INPUT,
        STEP_IDS.SESSION_SEQUENCE,
        STEP_IDS.SESSION_BUILD,
    ],
};

const STEP_COMPONENTS = {
    [STEP_IDS.FLOW_ENTRY]: FlowEntryStep,
    [STEP_IDS.CONTEXT]: ContextStep,
    [STEP_IDS.UNIT_SELECTION]: UnitSelectionStep,
    [STEP_IDS.UNIT_SITUATION]: UnitSituationStep,
    [STEP_IDS.SESSION_INPUT]: SessionInputStep,
    [STEP_IDS.SESSION_SEQUENCE]: SessionSequenceStep,
    [STEP_IDS.SESSION_BUILD]: SessionBuildStep,
};


const LOCALSTORAGE_KEYS_TO_CLEAR = [
    "sac_hash_step3",
    "sac_step2_data",
    "sac_step5_data",
    "sac_temas_generados",
    "seleccionPaso1",
    "seleccionPaso2",
    "seleccionPaso2_key",
    "seleccionPaso3",
    "sesionesPaso3",
    "sesionesPaso3_key",
    "temasPaso3",
    "temasPaso3_key",
    "ultimoMongoId",
];


const SACWizard = ({ userRole = "ADMIN" }) => {
    /* ---------- Limpieza inicial ---------- */
    useEffect(() => {
        const initialized = sessionStorage.getItem("sac_initialized");
        if (initialized) return;

        LOCALSTORAGE_KEYS_TO_CLEAR.forEach((key) =>
            localStorage.removeItem(key)
        );

        sessionStorage.setItem("sac_initialized", "1");
    }, []);

    // eslint-disable-next-line
    /* ---------- Flujo según rol ---------- */
    const flow = useMemo(() => FLOW_BY_ROLE[userRole] ?? FLOW_BY_ROLE.ADMIN, [userRole]);

    /* ---------- Paso actual ---------- */
    const [stepIndex, setStepIndex] = useState(0);
    const stepKey = flow[stepIndex];
    const StepComponent = STEP_COMPONENTS[stepKey];

    /* ---------- Estados globales ---------- */

    // eslint-disable-next-line
    const [selectores, setSelectores] = useState([]);
    const [unidadesSeleccionadas, setUnidadesSeleccionadas] = useState([]);
    const [inputsGenerados, setInputsGenerados] = useState([]);

    /* ---------- Navegación ---------- */
    const next = () =>
        setStepIndex((i) => Math.min(i + 1, flow.length - 1));

    const back = () => {
        if ((userRole === "ADMIN" || userRole === "DIRECTOR") && stepKey === STEP_IDS.SESSION_INPUT) {
            goTo(STEP_IDS.FLOW_ENTRY);
            return;
        }
        setStepIndex((i) => Math.max(i - 1, 0));
    };

    const goTo = (stepName) => {
        const index = flow.indexOf(stepName);
        if (index !== -1) setStepIndex(index);
    };

    const commonProps = {
        onNext: next,
        onBack: back,
        userRole,
        setSelectores,
        setUnidadesSeleccionadas,
        setInputsGenerados,
        unidadesSeleccionadas,
        inputsGenerados,
        data: inputsGenerados,
        setData: setInputsGenerados,
        goContextStep: () => goTo(STEP_IDS.CONTEXT),
        goUnitSelection: () => goTo(STEP_IDS.UNIT_SELECTION),
        goSessionInput: () => goTo(STEP_IDS.SESSION_INPUT),
        goGenerarUnidades: () => {
            goTo(STEP_IDS.CONTEXT);
        },

        goGenerarSesiones: () => {
            goTo(STEP_IDS.SESSION_INPUT);
        },

    };

    return (
        <Box sx={{ marginBottom: 2 }}>
            <Box
                sx={{
                    py: { xs: 3, md: 4 },
                    px: { xs: 2, md: 4 },
                }}>

                <HeroHeader
                    img={logoSac}
                    subtitle="PLANIFICADOR DE SESIONES"
                    title="Sesión de Aprendizaje SAC"
                    description="Diseña, estructura y genera sesiones de aprendizaje contextualizadas de manera guiada."
                    maxWidth="xxl"
                />
            </Box>

            <Container maxWidth="xxl" sx={sxPage}>
                {StepComponent && (
                    <StepComponent
                        {...commonProps}
                        {...(stepKey === STEP_IDS.SESSION_BUILD
                            ? { unidadesSeleccionadas: inputsGenerados }
                            : {})}
                    />
                )}
            </Container>
        </Box>
    );
};

export default SACWizard;
